import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Box,
  Typography,
  Chip,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import debounce from "lodash.debounce";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UserGroupAssignmentManager = ({ open, onClose, userId, userName }) => {
  const { empId, boId, roles, hasPermission } = useCurrentUser();
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [manualSelectedGroups, setManualSelectedGroups] = useState([]);

  const assignedUsersRef = useRef([]);
  useEffect(() => {
    assignedUsersRef.current = assignedGroups;
  }, [assignedGroups]);

  // ✅ Always fetch by userId (int)
  const refreshAssignedGroups = useCallback(async () => {
    if (!userId) return;
    setLoadingAssigned(true);
    try {
      const res = await fetch(`${BASE_URL}/api/userGroups/${userId}`);
      const data = await res.json();
      setAssignedGroups(data || []);
    } catch (err) {
      console.error("Failed to fetch assigned groups:", err);
      setAlert({ type: "error", msg: "Failed to load assigned groups" });
    } finally {
      setLoadingAssigned(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open) {
      refreshAssignedGroups();
      setSearchResults([]);
      setSearchQuery("");
      setSelectedGroups([]);
    }
  }, [open, refreshAssignedGroups]);

  // Auto-clear alert after 6 seconds
  useEffect(() => {
    if (!alert.msg) return;
    const timer = setTimeout(() => {
      setAlert({ type: "", msg: "" });
    }, 6000);
    return () => clearTimeout(timer);
  }, [alert.msg]);

  useEffect(() => {
    fetchAllGroups();
  }, []);

  // Auto-select assigned groups when dialog opens
  useEffect(() => {
    if (open && assignedGroups.length > 0) {
      setSelectedGroups(assignedGroups);
    }
  }, [open, assignedGroups]);

  const fetchAllGroups = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/groups/${boId}`);
      const data = await res.json();
      setAllGroups(data);
      setSearchResults(data); // default view = all groups
    } catch (err) {
      console.error("Error loading groups", err);
    }
  };

  //  Search API should also work with userId (not empId)
  const handleSearch = useCallback(
    debounce(async (query) => {
      if (!query?.trim()) {
        setSearchResults(allGroups);
        return;
      }
      setLoadingSearch(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/userGroups/${userId}/search?search=${query}` // 🔑 FIXED
        );
        const data = await res.json();
        const groups = data?.data || [];
        const assignedIds = new Set(
          assignedUsersRef.current.map((g) => g.group_id)
        );
        const filtered = groups.filter((g) => !assignedIds.has(g.group_id));
        setSearchResults(filtered);
      } catch (err) {
        console.error("Search failed:", err);
      } finally {
        setLoadingSearch(false);
      }
    }, 300),
    [userId, allGroups] // 🔑 added userId to deps
  );

  // ✅ Assign based on userId
  const handleAssignGroup = async (group) => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/userGroups/${userId}/${group.group_id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ boId, empId }),
        }
      );

      if (!response.ok) {
        let errorMsg = `Failed to assign (status ${response.status})`;
        try {
          const errData = await response.json();
          if (errData?.message) errorMsg = errData.message;
        } catch {
          const errText = await response.text();
          if (errText) errorMsg = errText;
        }
        throw new Error(errorMsg);
      }

      setAlert({ type: "success", msg: `Added ${userName} to ${group.name}` });
      refreshAssignedGroups();
      fetchAllGroups();
      setSearchResults("");
      setSearchQuery("");
    } catch (err) {
      console.error("Assign failed:", err);
      setAlert({ type: "error", msg: err.message || "Failed to add group" });
    }
  };

  const handleAssignSelectedGroups = async () => {
    for (const group of selectedGroups) {
      await handleAssignGroup(group);
    }
    setSelectedGroups([]);
  };

  // ✅ Remove also by userId
  const handleRemove = async (group) => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/userGroups/${userId}/groups/${group.group_id}`,
        { method: "DELETE" }
      );

      if (!response.ok) {
        let errorMsg = `Failed to remove (status ${response.status})`;
        try {
          const errData = await response.json();
          if (errData?.message) errorMsg = errData.message;
        } catch {
          const errText = await response.text();
          if (errText) errorMsg = errText;
        }
        throw new Error(errorMsg);
      }

      setAlert({
        type: "success",
        msg: `${userName} removed from ${group.name} `,
      });
      refreshAssignedGroups();
    } catch (err) {
      console.error("Remove failed:", err);
      setAlert({ type: "error", msg: err.message || "Failed to remove group" });
    }
  };

  const handleSelectGroup = (group, isSelected) => {
    setSelectedGroups((prev) =>
      isSelected
        ? [...prev, group]
        : prev.filter((g) => g.group_id !== group.group_id)
    );
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        Manage Groups for - <span>{userName || ""}</span>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          maxHeight: "70vh",
        }}
      >
        {alert.msg && (
          <Alert
            severity={alert.type}
            onClose={() => setAlert({ type: "", msg: "" })}
          >
            {alert.msg}
          </Alert>
        )}

        {/* Search Groups Section */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
            <Typography variant="subtitle1">Search & Add Groups</Typography>
            <TextField
              size="small"
              sx={{ width: "250px", height: 34 }}
              placeholder="Search groups..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                handleSearch(e.target.value);
              }}
              InputProps={{
                sx: { height: 36, paddingRight: 0 },
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Selected Groups Preview */}
          {selectedGroups.filter(
            (g) => !assignedGroups.some((a) => a.group_id === g.group_id)
          ).length > 0 && (
            <Box
              sx={{ mt: 1, mb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}
            >
              {selectedGroups
                .filter(
                  (g) => !assignedGroups.some((a) => a.group_id === g.group_id)
                )
                .map((group) => (
                  <Chip
                    key={group.group_id}
                    label={group.name}
                    onDelete={() => handleSelectGroup(group, false)}
                    color="primary"
                  />
                ))}
              <Button
                variant="contained"
                size="small"
                sx={{
                  fontWeight: "bold",
                  borderRadius: "8px",
                  px: 2.5,
                  height: 36,
                  backgroundColor: "#2DB0E4",
                }}
                startIcon={<Add />}
                onClick={handleAssignSelectedGroups}
              >
                Add Selected
              </Button>
            </Box>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
           <Box sx={{ maxHeight: 200, overflowY: "auto", mt: 1 }}>
             <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchResults.map((group) => (
                  <TableRow key={group.group_id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedGroups.some(
                          (g) => g.group_id === group.group_id
                        )}
                        disabled={assignedGroups.some(
                          (a) => a.group_id === group.group_id
                        )}
                        onChange={(e) =>
                          handleSelectGroup(group, e.target.checked)
                        }
                      />
                    </TableCell>
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{group.description || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          )}
        </Box>

        {/* Assigned Groups Section */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            maxHeight: 400,
            position: "relative",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: "background.paper",
              zIndex: 2,
              py: 1,
              mt:-1,
            }}
          >
            Assigned Groups
          </Typography>
          {loadingAssigned ? (
            <CircularProgress size={24} />
          ) : assignedGroups.length === 0 ? (
            <p align="center">No groups assigned</p>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{
                      position: "sticky",
                      top: 32,
                      backgroundColor: "background.paper",
                      zIndex: 1,
                    }}
                  >
                    Action
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      top: 32,
                      backgroundColor: "background.paper",
                      zIndex: 1,
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      top: 32,
                      backgroundColor: "background.paper",
                      zIndex: 1,
                    }}
                  >
                    Description
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedGroups.map((group) => (
                  <TableRow key={group.group_id}>
                    <TableCell align="left">
                      <IconButton
                        color="error"
                        onClick={() => handleRemove(group)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{group.description || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserGroupAssignmentManager;
